import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { blogArticles, type BlogArticle } from "@/data/blogArticles";
import { additionalBlogArticles } from "@/data/additionalBlogArticles";
import { isKeeper } from "@/data/blogKeepers";

const ALL_ARTICLES: BlogArticle[] = [...blogArticles, ...additionalBlogArticles];

export interface RelatedArticlesProps {
  /** The pillar's topic keywords, used to rank blog articles by keyword overlap. */
  topics: string[];
  /** Optional explicit slugs to guarantee inclusion at the top. */
  featuredSlugs?: string[];
  /** Article slug to exclude (usually the current page, if it doubles as an article). */
  excludeSlug?: string;
  /** Max cards to show. Defaults to 3. */
  limit?: number;
  heading?: string;
  eyebrow?: string;
}

/** Ranks by count of shared keyword tokens (case-insensitive). */
const scoreArticle = (article: BlogArticle, topics: string[]): number => {
  const bag = new Set(
    [...article.keywords, article.category, article.title]
      .join(" ")
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean),
  );
  let score = 0;
  for (const topic of topics) {
    for (const token of topic.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)) {
      if (bag.has(token)) score += 1;
    }
  }
  return score;
};

const RelatedArticles = ({
  topics,
  featuredSlugs = [],
  excludeSlug,
  limit = 3,
  heading = "Related articles",
  eyebrow = "Keep reading",
}: RelatedArticlesProps) => {
  const bySlug = new Map(ALL_ARTICLES.map((a) => [a.slug, a]));

  const featured = featuredSlugs
    .map((slug) => bySlug.get(slug))
    .filter((a): a is BlogArticle => Boolean(a) && a!.slug !== excludeSlug);

  const ranked = ALL_ARTICLES
    .filter((a) => a.slug !== excludeSlug && isKeeper(a.slug) && !featuredSlugs.includes(a.slug))
    .map((a) => ({ article: a, score: scoreArticle(a, topics) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.article);

  const picks = [...featured, ...ranked].slice(0, limit);
  if (picks.length === 0) return null;

  return (
    <section
      aria-labelledby="related-articles-heading"
      className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20"
    >
      <div className="max-w-5xl mx-auto">
        <div className="mb-10 text-center">
          <div className="text-[11px] tracking-[0.22em] uppercase text-primary mb-3">
            {eyebrow}
          </div>
          <h2
            id="related-articles-heading"
            className="font-display text-3xl sm:text-4xl text-foreground tracking-tight"
          >
            {heading}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {picks.map((article) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="group flex flex-col rounded-2xl border border-border bg-surface overflow-hidden hover:border-primary/40 hover:shadow-md transition-all"
            >
              {article.heroImage && (
                <div className="aspect-[16/10] bg-muted overflow-hidden">
                  <img
                    src={article.heroImage}
                    alt={article.heroAlt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              )}
              <div className="p-5 flex flex-col flex-1">
                <div className="text-[11px] tracking-[0.16em] uppercase text-muted-foreground mb-2">
                  {article.category} · {article.readTime}
                </div>
                <h3 className="font-display text-lg text-foreground leading-snug mb-2 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">
                  {article.excerpt}
                </p>
                <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Read
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedArticles;
